/**
 * Core: App proxy
 * -----------------------------------------------------------------------------
 * Any injected <script> tags will break the Vue container. Because of this,
 * app sections and app proxy pages need a workaround.
 *
 * - Teleport site footer to the end of the HTML body on app proxy pages.
 * - Teleport all app sections outside the Vue container until Vue is done
 * rendering.
 * - Return app sections to their original spots after.
 *
 */

import classes from '~/config/classes'
import { values as timings } from '~/config/timings'

classes.appBlock = 'shopify-app-block'

const selectors = {
  footer: '#shopify-section-site-footer',
  placeholder: '[js-app-proxy="placeholder"]',
  sections: '.shopify-section',
}

const settings = {
  appSections: {},
}

export default () => {

  let nodeSelectors = {}

  function setNodeSelectors() {
    const appSections = [...document.querySelectorAll(selectors.sections)].filter(section => {
      if (!section.children) {
        return
      }

      const children = [...section.children]
      return children.find((child) => {
        if (!child.classList) {
          return
        }

        return child.classList?.contains(classes.appBlock)
      })
    })

    nodeSelectors = {
      footer: document.querySelector(selectors.footer),
      placeholders: document.querySelectorAll(selectors.placeholder),
      appSections,
    }
  }

  /**
   * Move footer to end of the body if it's an app proxy page.
   */
  function handleAppProxy() {
    if (!isAppProxy()) {
      return
    }

    if (nodeSelectors.footer) {
      teleportContent(document.body, nodeSelectors.footer)
    }
    removePlaceholders()
  }

  /**
   * Move all app sections to the end of the body.
   */
  function handleAppSections() {
    storeAppPositions()

    nodeSelectors.appSections.forEach(section => {
      teleportContent(document.body, section)
    })
  }

  /**
   * Store app positions
   */
  function storeAppPositions() {
    nodeSelectors.appSections.forEach(section => {
      settings.appSections[section.id] = {
        previousElement: section.previousElementSibling?.id,
        parentElement: section.parentElement?.id,
      }
    })
  }

  /**
   * Move all app sections back to their original placements.
   */
  function resetAppSectionPlacements() {
    if (!nodeSelectors.appSections.length) {
      return
    }

    nodeSelectors.appSections.forEach((section) => {
      const { previousElement, parentElement } = settings.appSections[section.id] || {}

      if (previousElement) {
        const element = document.querySelector(`#${previousElement}`)

        if (element) {
          element.insertAdjacentElement('afterend', section)
        }
      }
      else if (parentElement) {
        const element = document.querySelector(`#${parentElement}`)

        if (element) {
          element.insertAdjacentElement('afterbegin', section)
        }
      }
    })
  }

  /**
   * Check if page is an app proxy page.
   * @returns {Boolean}
   */
  function isAppProxy() {
    return window.cnvs.routes.appProxy?.some(proxy => window.location.pathname.includes(proxy))
  }

  /**
   * Remove any app placeholders. Placeholders cover the page until the
   * page is finished shifting.
   */
  function removePlaceholders() {
    nodeSelectors.placeholders.forEach(placeholder => {
      placeholder.classList.remove(classes.updating)
      setTimeout(() => {
        placeholder.parentNode.removeChild(placeholder)
      }, timings.s)
    })
  }

  /**
   * Teleport content to a different container.
   */
  function teleportContent(to, element) {
    to.appendChild(element)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    setNodeSelectors,
    handleAppProxy,
    handleAppSections,
    resetAppSectionPlacements,
  })
}
